import { useState, useEffect } from 'react';
import { SelyoWorkspace } from './SelyoWorkspace';
import { AdminKeygen } from './AdminKeygen';

interface DownloadStat {
  platform: string;
  label: string;
  emoji: string;
  count: number | null;
}

interface TelemetryMetrics {
  successfulSalesCount: number;
  successfulSalesVolume: number;
  cryptoSignaturesProcessed: number;
  pendingOfflineSyncs: number;
}

const WORKER_BASE = 'https://nameless-math-e510.cruzfind.workers.dev';

export function AdminDashboard() {
  const [showWorkspace, setShowWorkspace] = useState(false);
  const [stats, setStats] = useState<DownloadStat[]>([
    { platform: 'Android_APK', label: 'Android APK', emoji: '📥', count: null },
    { platform: 'PWA_Install', label: 'PWA Container', emoji: '💻', count: null },
    { platform: 'Desktop_Web_App', label: 'Desktop Web', emoji: '🖥️', count: null },
  ]);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showKeygen, setShowKeygen] = useState(false);

  // Live Telemetry Stream State
  const [metrics, setMetrics] = useState<TelemetryMetrics>({
    successfulSalesCount: 0,
    successfulSalesVolume: 0,
    cryptoSignaturesProcessed: 0,
    pendingOfflineSyncs: 0,
  });
  const [systemHealth, setSystemHealth] = useState<string>('connecting');

  const fetchStats = async () => {
    setIsRefreshing(true);
    setFetchError(null);
    try {
      const results = await Promise.allSettled(
        stats.map((s) =>
          fetch(`${WORKER_BASE}/stats?platform=${s.platform}`, { mode: 'cors' })
            .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        )
      );
      setStats((prev) =>
        prev.map((s, i) => {
          const r = results[i];
          if (r && r.status === 'fulfilled' && typeof r.value?.count === 'number') {
            return { ...s, count: r.value.count };
          }
          return s;
        })
      );
    } catch {
      setFetchError('Worker endpoint unreachable. Stats will update when connected.');
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStats();
    
    // Connect to Server-Sent Events (SSE) telemetry stream
    const eventSource = new EventSource('/api/v1/telemetry/stream');
    
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.metrics) {
          setMetrics(data.metrics);
        }
        if (data.systemHealth) {
          setSystemHealth(data.systemHealth);
        }
      } catch (err) {
        console.error('Error parsing SSE telemetry payload:', err);
      }
    };

    eventSource.onerror = () => {
      setSystemHealth('unreachable');
    };

    return () => {
      eventSource.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6 max-w-5xl mx-auto px-4 py-6 bg-[#F8F9FA] rounded-[32px] border border-slate-200/60 p-4 md:p-6 shadow-sm">
      {/* Utility Header Strip */}
      <div className="w-full bg-white border border-slate-200 text-slate-800 rounded-2xl p-4 flex flex-col md:flex-row items-center justify-between gap-4 shadow-sm hover:shadow-md transition-all duration-300 ease-in-out relative z-50">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-700 bg-[#F8F9FA] border border-slate-200 px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-sm">
            🛡️ Administrative Master Session: <span className="text-inc-green font-mono font-bold">nisiryancruz@gmail.com</span>
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setShowWorkspace(!showWorkspace)}
            className="text-xs font-bold bg-inc-green hover:bg-inc-green/90 text-white px-4 py-2.5 rounded-xl transition-all shadow-sm hover:shadow-md duration-300 ease-in-out active:scale-[0.98] cursor-pointer"
          >
            {showWorkspace ? '📊 Buksan ang Admin Control Deck' : '📱 Buksan ang Sales Invoicer Manual Ledger'}
          </button>
          <button
            type="button"
            onClick={() => {
              localStorage.removeItem('selyo_user_email');
              localStorage.removeItem('selyo_user_role');
              localStorage.removeItem('cf_auth_token_signature');
              window.location.reload();
            }}
            className="text-xs font-bold bg-inc-red hover:bg-inc-red/90 text-white px-4 py-2.5 rounded-xl transition-all shadow-sm hover:shadow-md duration-300 ease-in-out active:scale-[0.98] cursor-pointer"
          >
            🛑 Log Out Terminal Session
          </button>
        </div>
      </div>

      {showWorkspace ? (
        <div className="bg-white rounded-[24px] border border-slate-200/80 shadow-sm hover:shadow-md transition-all duration-300 ease-in-out overflow-hidden p-1">
          <SelyoWorkspace />
        </div>
      ) : (
        <div className="space-y-8 bg-white text-slate-800 relative overflow-hidden rounded-[24px] border border-slate-200 p-6 md:p-8 shadow-sm hover:shadow-md transition-all duration-300 ease-in-out border-t-[6px] border-t-inc-red">
          
          {/* Background ambient glowing spheres */}
          <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-[radial-gradient(circle,rgba(0,135,81,0.03),transparent_60%)] pointer-events-none" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-[radial-gradient(circle,rgba(210,38,48,0.02),transparent_50%)] pointer-events-none" />

          {/* Header Deck */}
          <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-b border-slate-100 pb-6">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[10px] font-extrabold uppercase tracking-widest text-inc-green">
                  Platform Control Deck
                </span>
                <span className={`px-2 py-0.5 text-[8px] font-black rounded-full uppercase tracking-wider ${
                  systemHealth === 'nominal' 
                    ? 'bg-inc-green/10 border border-inc-green/20 text-inc-green' 
                    : systemHealth === 'connecting'
                    ? 'bg-amber-500/10 border border-amber-500/20 text-amber-600 animate-pulse'
                    : 'bg-inc-red/10 border border-inc-red/20 text-inc-red'
                }`}>
                  {systemHealth === 'nominal' ? 'System Nominal' : systemHealth === 'connecting' ? 'Connecting Stream...' : 'Telemetry Offline'}
                </span>
              </div>
              <h1 className="font-display text-2xl font-black text-slate-900 tracking-tight">
                Selyo Business Automation Control Room
              </h1>
              <p className="text-[10px] text-slate-500 uppercase tracking-widest font-mono mt-1">
                CruzFind Software Development Services · Ramon, Isabela · <span className="font-semibold text-slate-700">TIN: 466-338-057-0000</span>
              </p>
            </div>
            <button
              type="button"
              onClick={fetchStats}
              disabled={isRefreshing}
              className="text-xs font-bold bg-inc-green hover:bg-inc-green/90 text-white px-5 py-3 rounded-xl transition-all disabled:opacity-50 active:scale-[0.98] shadow-md shadow-inc-green/15 shrink-0 self-stretch sm:self-auto hover:scale-[1.01]"
            >
              {isRefreshing ? '↻ Refreshing...' : '↻ Refresh Stats'}
            </button>
          </div>

          {/* Row 1 — Live Transaction Telemetry Stream */}
          <section className="relative z-10 bg-[#F8F9FA] border border-slate-200/80 rounded-3xl p-6 space-y-5 shadow-sm hover:shadow-md transition-all duration-300 ease-in-out">
            <h2 className="text-[10px] font-extrabold uppercase tracking-widest text-inc-green">
              📡 Real-Time SSE Telemetry Monitor
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              
              <div className="p-5 bg-white border border-slate-200 rounded-2xl text-center hover:border-inc-green/45 shadow-sm hover:shadow-md transition-all duration-300 ease-in-out">
                <div className="text-2xl mb-1">💰</div>
                <div className="font-display text-xl font-black text-slate-900 tracking-tight tabular-nums">
                  ₱{metrics.successfulSalesVolume.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="text-[8px] text-slate-500 mt-2 uppercase tracking-widest font-black">
                  Successful Sales Volume
                </div>
              </div>

              <div className="p-5 bg-white border border-slate-200 rounded-2xl text-center hover:border-inc-green/45 shadow-sm hover:shadow-md transition-all duration-300 ease-in-out">
                <div className="text-2xl mb-1">🧾</div>
                <div className="font-display text-xl font-black text-slate-900 tracking-tight tabular-nums">
                  {metrics.successfulSalesCount}
                </div>
                <div className="text-[8px] text-slate-500 mt-2 uppercase tracking-widest font-black">
                  Total Invoices Issued
                </div>
              </div>

              <div className="p-5 bg-white border border-slate-200 rounded-2xl text-center hover:border-inc-green/45 shadow-sm hover:shadow-md transition-all duration-300 ease-in-out">
                <div className="text-2xl mb-1">🔑</div>
                <div className="font-display text-xl font-black text-slate-900 tracking-tight tabular-nums">
                  {metrics.cryptoSignaturesProcessed}
                </div>
                <div className="text-[8px] text-slate-500 mt-2 uppercase tracking-widest font-black">
                  Crypto Signatures Processed
                </div>
              </div>

              <div className="p-5 bg-white border border-slate-200 rounded-2xl text-center hover:border-inc-green/45 shadow-sm hover:shadow-md transition-all duration-300 ease-in-out">
                <div className="text-2xl mb-1">🔄</div>
                <div className="font-display text-xl font-black text-slate-900 tracking-tight tabular-nums">
                  {metrics.pendingOfflineSyncs}
                </div>
                <div className="text-[8px] text-slate-500 mt-2 uppercase tracking-widest font-black">
                  Pending Sync Queue
                </div>
              </div>

            </div>
          </section>

          {/* Row 2 — Total Platform Downloads Monitor */}
          <section className="relative z-10 bg-[#F8F9FA] border border-slate-200/80 rounded-3xl p-6 space-y-5 shadow-sm hover:shadow-md transition-all duration-300 ease-in-out">
            <h2 className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">
              📊 Total Platform Downloads Monitor
            </h2>
            {fetchError && (
              <p className="text-[11px] text-inc-red bg-inc-red/5 border border-inc-red/20 rounded-xl px-4 py-3">
                {fetchError}
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {stats.map(({ platform, label, emoji, count }) => (
                <div
                  key={platform}
                  className="flex flex-col items-center justify-center p-6 bg-white border border-slate-200 rounded-2xl text-center hover:border-inc-green/45 hover:scale-[1.01] hover:shadow-md transition-all duration-300 ease-in-out shadow-sm group"
                >
                  <span className="text-3xl mb-2 group-hover:scale-110 transition-transform duration-300">{emoji}</span>
                  <div className="font-display text-2xl font-black text-slate-900 tracking-tight tabular-nums">
                    {count === null ? (
                      <span className="text-slate-400 text-lg animate-pulse">—</span>
                    ) : (
                      count.toLocaleString()
                    )}
                  </div>
                  <div className="text-[9px] text-slate-500 mt-2 uppercase tracking-widest font-black">
                    {label}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Row 3 — Voucher Block Generator */}
          <section className="relative z-10 bg-[#F8F9FA] border border-slate-200/80 rounded-3xl p-6 space-y-4 shadow-sm hover:shadow-md transition-all duration-300 ease-in-out text-left">
            <h2 className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">
              🔑 Voucher Block Generator
            </h2>
            <p className="text-xs text-slate-600 leading-relaxed">
              Generate cryptographic activation keys bound to the registered TIN{' '}
              <span className="font-mono font-semibold text-inc-green">466-338-057-0000</span> at the ₱1,499
              price tier. Powered by the <span className="font-semibold text-slate-800">mintCore</span> deterministic engine.
            </p>
            {showKeygen ? (
              <div className="mt-4 border-t border-slate-200/60 pt-4">
                <AdminKeygen />
                <button
                  type="button"
                  onClick={() => setShowKeygen(false)}
                  className="mt-4 text-[10px] uppercase font-bold text-slate-500 hover:text-slate-800 transition-colors"
                >
                  Close Vault
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowKeygen(true)}
                className="inline-flex items-center gap-2 text-xs font-bold bg-white hover:bg-slate-50 text-slate-800 hover:text-slate-950 px-5 py-3 rounded-xl transition-all shadow-sm hover:shadow-md border border-slate-200 hover:border-inc-green/30 hover:scale-[1.01] duration-300"
              >
                🔐 Open Keygen Vault →
              </button>
            )}
          </section>

          {/* Row 4 — Active Application Modules Deck */}
          <section className="relative z-10 bg-[#F8F9FA] border border-slate-200/80 rounded-3xl p-6 space-y-5 shadow-sm hover:shadow-md transition-all duration-300 ease-in-out text-left">
            <h2 className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">
              🧩 Active Application Modules Deck
            </h2>
            <div className="space-y-3">
              {/* Selyo */}
              <div className="flex items-center justify-between p-5 bg-white border border-slate-200 rounded-2xl transition-all duration-300 hover:border-inc-green/30 hover:shadow-sm">
                <div>
                  <div className="text-sm font-bold text-slate-800">Selyo</div>
                  <div className="text-[10px] text-slate-500 mt-1">
                    EOPT Act · Local-First Tax &amp; Invoicing Engine · powered by CruzFind
                  </div>
                </div>
                <span className="text-[8px] font-black bg-inc-green/10 border border-inc-green/20 text-inc-green px-3 py-1 rounded-full uppercase tracking-wider shrink-0">
                  Online
                </span>
              </div>

              {/* Placeholder module slot */}
              <button
                type="button"
                onClick={() =>
                  alert(
                    'Future Core App Module provisioning coming soon. Contact the development team to register a new module under the CruzFind umbrella.'
                  )
                }
                className="w-full flex items-center justify-center gap-2 p-5 border-2 border-dashed border-slate-200 bg-white/50 rounded-2xl text-xs text-slate-450 hover:border-inc-green/20 hover:bg-white hover:text-slate-650 hover:shadow-sm transition-all duration-300"
              >
                <span className="text-base">＋</span>
                Add Future Core App Module
              </button>
            </div>
          </section>

          {/* Footer */}
          <p className="text-[10px] text-slate-400 text-center pb-4 tracking-wide font-display">
            IPO Application No: 22026050461A · All systems nominal
          </p>
        </div>
      )}
    </div>
  );
}
